import { useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';
import { api } from '../../lib/api';
import { ResultView, Lines, Flags, Money, Empty, DEAL_ROLES, parseCents, inputCls, btnCls, cardCls, thCls, tdCls, fmtDate } from './shared';

// What a version pays, read only: on a real deal from Books, on the payments received so far,
// or on a typed amount. Nothing here writes to deals or payments.

const START_OPTIONS = [
  ['opening', 'Paybacks start from the amounts in these terms'],
  ['after_received', 'Paybacks as they stand after the payments received so far'],
];

export default function ScenariosTab({ detail, version, termsDirty = false }) {
  const [kind, setKind] = useState('deal');
  const tabs = [['deal', 'A real deal'], ['payments', 'Payments received'], ['whatif', 'A typed amount']];
  return (
    <div className="space-y-3">
      <p className="text-sm text-[var(--text-secondary)]">What version {version.version_no} ({version.status}) would pay. Read only: nothing here is saved or paid.</p>
      {termsDirty && <p className="rounded-lg bg-amber-50 p-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">You have unsaved changes on the Terms tab. These figures use the saved draft; save it to see your changes here.</p>}
      <div className="flex flex-wrap gap-1">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} className={`rounded-lg px-3 py-1.5 text-sm ${kind === k ? 'bg-primary text-white' : 'border border-[var(--border)] text-[var(--text-primary)]'}`}>{l}</button>
        ))}
      </div>
      {kind === 'deal' && <DealScenario detail={detail} version={version} />}
      {kind === 'payments' && <PaymentsScenario detail={detail} version={version} />}
      {kind === 'whatif' && <WhatIf key={`${version.id}:${version.updated_at}`} detail={detail} version={version} />}
    </div>
  );
}

const base = (detail, version) => `/partner-comp/agreements/${detail.agreement.id}/versions/${version.id}/scenarios`;

function DealScenario({ detail, version }) {
  const [deals, setDeals] = useState(null);
  const [dealId, setDealId] = useState('');
  const [start, setStart] = useState('opening');
  const [months, setMonths] = useState('0');
  const [out, setOut] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api.get('/partner-comp/deals').then((d) => setDeals(d.deals)).catch((e) => setError(e.message)); }, []);
  useEffect(() => {
    if (!dealId) { setOut(null); return; }
    let live = true;
    const q = new URLSearchParams({ deal_id: dealId, start, overhead_months: months || '0' });
    api.get(`${base(detail, version)}/deal?${q}`).then((r) => { if (live) { setOut(r); setError(''); } }).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [dealId, start, months, version.id, version.updated_at]);

  if (deals && !deals.length) return <Empty>No deals in Books yet. Deals (with their roles and direct costs) are set up in Books, Deal payouts.</Empty>;
  return (
    <div className={`${cardCls} space-y-3`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <select className={inputCls} value={dealId} onChange={(e) => setDealId(e.target.value)} aria-label="Deal">
          <option value="">Choose a deal</option>
          {(deals || []).map((d) => <option key={d.id} value={d.id}>{d.name}{d.customer ? ` (${d.customer})` : ''}</option>)}
        </select>
        <select className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} aria-label="Paybacks start">
          {START_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="flex items-center gap-1">Overhead months <input className={`${inputCls} w-16 text-right`} value={months} onChange={(e) => setMonths(e.target.value.replace(/\D/g, ''))} aria-label="Overhead months" /></label>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {out && (
        <>
          <p className="text-xs text-[var(--text-secondary)]">
            Roles on this deal: {DEAL_ROLES.map((r) => `${r.label} ${out.deal.assignments?.[r.key] || 'nobody'}`).join(', ')}. Received <Money cents={out.deal.receipts_cents} />, direct costs <Money cents={out.deal.costs_cents} />{out.deal.contract_value_cents ? <>, contract <Money cents={out.deal.contract_value_cents} /></> : null}. {out.start_note}
          </p>
          {out.empty && <Empty>{out.empty}</Empty>}
          {out.views.map((v) => (
            <div key={v.key}>
              <h4 className="mb-2 text-sm font-semibold text-[var(--text-primary)]">{v.label}</h4>
              <ResultView result={v.result} partners={detail.partners} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function PaymentsScenario({ detail, version }) {
  const [out, setOut] = useState(null);
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    api.get(`${base(detail, version)}/payments`).then((r) => { if (live) { setOut(r); setError(''); } }).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [version.id, version.updated_at]);
  if (error) return <p className="text-sm text-rose-600">{error}</p>;
  if (!out) return <p className="text-sm text-[var(--text-secondary)]">Loading...</p>;
  if (!out.payments.length) return <Empty>No payments received yet (payments are recorded on invoices).</Empty>;
  return (
    <div className={`${cardCls} space-y-2`}>
      <p className="text-xs text-[var(--text-secondary)]">{out.basis}</p>
      <PaymentsTable rows={out.payments} partners={detail.partners} open={open} setOpen={setOpen} totals={out.totals} />
      {out.paybacks_owed_after.some((p) => p.owed_cents > 0) && (
        <p className="text-xs text-[var(--text-secondary)]">Paybacks still owed after these payments: {out.paybacks_owed_after.filter((p) => p.owed_cents > 0).map((p) => `${p.label} $${(p.owed_cents / 100).toFixed(2)}`).join('; ')}.</p>
      )}
    </div>
  );
}

const STATUS = {
  computed: { label: 'Computed', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  no_locked_agreement: { label: 'No locked agreement', cls: 'bg-gray-200 text-gray-800 dark:bg-slate-700 dark:text-slate-200' },
  needs_deal: { label: 'Needs a deal', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
};

/** Payments with each partner's share; click a row for its lines. Shared with the Shares tab. */
export function PaymentsTable({ rows, partners, open, setOpen, totals, showVersion = false }) {
  const names = partners.map((p) => p.partner);
  const take = (r, n) => r.lines.filter((l) => l.partner === n && (l.kind === 'role_fee' || l.kind === 'remainder')).reduce((s, l) => s + l.amount_cents, 0);
  const kindSum = (r, k) => r.lines.filter((l) => l.kind === k).reduce((s, l) => s + l.amount_cents, 0);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className={thCls}>Date</th><th className={thCls}>Invoice / deal</th><th className={`${thCls} text-right`}>Amount</th>
            {showVersion && <th className={thCls}>Version</th>}
            <th className={thCls}>Status</th>
            <th className={`${thCls} text-right`}>Costs</th><th className={`${thCls} text-right`}>Paybacks</th><th className={`${thCls} text-right`}>Overhead</th>
            {names.map((n) => <th key={n} className={`${thCls} text-right`}>{n}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <FragmentRow key={r.payment_id} r={r} open={open === r.payment_id} onToggle={() => setOpen(open === r.payment_id ? null : r.payment_id)} names={names} take={take} kindSum={kindSum} showVersion={showVersion} />
          ))}
          {totals && (
            <tr className="border-t-2 border-[var(--border)] font-semibold">
              <td className={tdCls} colSpan={showVersion ? 8 : 7}>Partners' take (fees + remainder)</td>
              {names.map((n) => <td key={n} className={`${tdCls} text-right`}><Money cents={totals[n]?.take_cents || 0} /></td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function FragmentRow({ r, open, onToggle, names, take, kindSum, showVersion }) {
  const st = STATUS[r.status] || STATUS.computed;
  return (
    <>
      <tr className="cursor-pointer border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800" onClick={onToggle}>
        <td className={tdCls}>{fmtDate(r.paid_on)}</td>
        <td className={tdCls}>{r.invoice_number || '-'}<div className="text-xs text-[var(--text-secondary)]">{r.deal_name || 'no deal linked'}</div></td>
        <td className={`${tdCls} text-right`}><Money cents={r.amount_cents} /></td>
        {showVersion && <td className={tdCls}>{r.version_no ? `v${r.version_no}` : '-'}</td>}
        <td className={tdCls}><span className={`rounded px-1.5 py-0.5 text-xs ${st.cls}`}>{st.label}</span>{r.detail?.provisional && <div className="mt-0.5 text-[11px] text-[var(--text-secondary)]" title="The deal is not final: later payments or costs on it change this row">provisional</div>}</td>
        {r.status === 'computed' ? (
          <>
            <td className={`${tdCls} text-right`}><Money cents={kindSum(r, 'direct_cost')} /></td>
            <td className={`${tdCls} text-right`}><Money cents={kindSum(r, 'payback')} /></td>
            <td className={`${tdCls} text-right`}><Money cents={kindSum(r, 'overhead')} /></td>
            {names.map((n) => <td key={n} className={`${tdCls} text-right`}><Money cents={take(r, n)} /></td>)}
          </>
        ) : <td className={`${tdCls} text-xs text-[var(--text-secondary)]`} colSpan={3 + names.length}>{r.detail?.message}</td>}
      </tr>
      {open && r.status === 'computed' && (
        <tr><td colSpan={(showVersion ? 8 : 7) + names.length} className="bg-gray-50 p-3 dark:bg-slate-800/50"><Lines lines={r.lines} /><div className="mt-2"><Flags result={{ ...r.detail, lines: r.lines }} /></div></td></tr>
      )}
    </>
  );
}

function WhatIf({ detail, version }) {
  const dealRoles = (version.terms.roles || []).filter((r) => r.holder === 'deal');
  const [amount, setAmount] = useState('');
  const [cost, setCost] = useState('');
  const [holders, setHolders] = useState({});
  const [months, setMonths] = useState('0');
  const [start, setStart] = useState('opening');
  const [out, setOut] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    const a = parseCents(amount); const c = parseCents(cost || '0');
    if (a == null || a <= 0) { setError('Type an amount in dollars, like 30000'); return; }
    if (c == null) { setError('Direct costs must be dollars'); return; }
    setBusy(true); setError('');
    try { setOut(await api.post(`${base(detail, version)}/what-if`, { amount_cents: a, direct_cost_cents: c, holders, overhead_months: Number(months || 0), start })); } catch (e) { setError(e.message); setOut(null); } finally { setBusy(false); }
  };
  return (
    <div className={`${cardCls} space-y-3`}>
      <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <label className="flex items-center gap-2">Amount received $<input className={`${inputCls} w-36 text-right`} value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount received" /></label>
        <label className="flex items-center gap-2">Direct job costs $<input className={`${inputCls} w-36 text-right`} value={cost} onChange={(e) => setCost(e.target.value)} aria-label="Direct job costs" /></label>
        {dealRoles.map((r) => (
          <label key={r.key} className="flex items-center gap-2">{r.label}
            <select className={inputCls} value={holders[r.key] || ''} onChange={(e) => setHolders((h) => ({ ...h, [r.key]: e.target.value || null }))} aria-label={r.label}>
              <option value="">Nobody</option>
              {detail.partners.map((p) => <option key={p.partner} value={p.partner}>{p.partner}</option>)}
            </select>
          </label>
        ))}
        <label className="flex items-center gap-2">Overhead months <input className={`${inputCls} w-16 text-right`} value={months} onChange={(e) => setMonths(e.target.value.replace(/\D/g, ''))} aria-label="Overhead months" /></label>
        <select className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} aria-label="Paybacks start">
          {START_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <button className={btnCls} disabled={busy} onClick={go}><Calculator size={15} /> Show the split</button>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {out && (<><p className="text-xs text-[var(--text-secondary)]">{out.start_note}</p><ResultView result={out.result} partners={detail.partners} /></>)}
    </div>
  );
}
